import { AccountListItemData } from '@deqo/api-bindings';
import { ActionIcon, Button, Stack } from '@mantine/core';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'wouter';
import { Page } from './components/Page';
import { PageCard } from './components/PageCard';
import { PageCardLogo } from './components/PageCardLogo';
import { SplashPane } from './components/SplashPane';
import { useAccountListQuery } from './hooks/useAccountListQuery';
import { useIdentify } from './hooks/useIdentify';
import { Colors } from './patterns/Colors';

const humanizeRole = (role: string) =>
    role.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

export const OrganizationsPage = () => {

    useIdentify();

    const { data: accounts, isLoading, error } = useAccountListQuery();

    return <Page>
        <PageCard splash={<SplashPane />}>
            <PageCardLogo />
            {isLoading && <Subhead> Loading organizations…</Subhead>}
            {error && <ErrorText>Failed to load organizations: {(error as Error).message}</ErrorText>}
            {accounts && (accounts.length > 0
                ? <AccountList accounts={accounts} />
                : <NoAccounts />)}
        </PageCard>
    </Page>;
};

const AccountList = ({ accounts }: { accounts: AccountListItemData[] }) => {

    const [showLeave, setShowLeave] = useState(false);

    return (
        <>
            <Heading>Your organizations</Heading>
            <Subhead>Pick an organization to continue.</Subhead>
            <ScrollList>
                <Stack gap="xs">
                    {accounts.map(account => (
                        <ItemRow key={account.id}>
                            {/* /{accountId} lives in the main app, not the onboarding router, so navigate full-page. */}
                            <AccountRow href={`/${account.id}`} tabIndex={0}>
                                <AccountMain>
                                    <Arrow><ArrowRightIcon /></Arrow>
                                    <AccountLabel>{account.label}</AccountLabel>
                                </AccountMain>
                                <AccountRole>{humanizeRole(account.role)}</AccountRole>
                            </AccountRow>
                            {showLeave && (
                                // app-server route, so navigate full-page.
                                <ActionIcon
                                    component="a"
                                    href={`/leave/${account.id}`}
                                    variant="default"
                                    size="lg"
                                    aria-label={`Leave ${account.label}`}
                                >
                                    <CrossIcon />
                                </ActionIcon>
                            )}
                        </ItemRow>
                    ))}
                </Stack>
            </ScrollList>
            <LeaveSection>
                <Button size="xs" variant="default" onClick={() => setShowLeave(v => !v)}>
                    {showLeave ? 'Hide leave organization options' : 'Show leave organization options'}
                </Button>
                {/* app-server route, so navigate full-page. */}
                <Button size="xs" variant="default" onClick={() => { window.location.href = '/account/close'; }}>
                    Delete my profile
                </Button>
            </LeaveSection>
        </>
    );
};

const NoAccounts = () => (
    <>
        <Heading>Get started with Spreo</Heading>
        <Subhead>Create your own organization, or join an existing one.</Subhead>
        <Stack gap="sm">
            <Button component={Link} href="/organizations/create" color="primary" fullWidth>
                Create organization
            </Button>
            <Button component={Link} href="/organizations/join" variant="default" fullWidth>
                Join organization
            </Button>
        </Stack>
        {/* app-server route, so navigate full-page. */}
        <DeleteProfileLink href="/account/close">Delete my profile</DeleteProfileLink>
    </>
);

const DeleteProfileLink = styled.a`
    margin-top: auto;
    align-self: flex-start;
    font-size: 0.8125rem;
    color: ${Colors.primary};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const Heading = styled.h1`
    font-size: 1.25rem;
    font-weight: 500;
    color: ${Colors.text};
    margin: 0 0 0.5rem;
`;

const Subhead = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    opacity: 0.7;
    margin: 0 0 1rem;
`;

const ScrollList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /* Breathing room so hover borders and the scrollbar don't clip the rows. */
    padding: 0.25rem 0.5rem;
    margin: -0.25rem -0.5rem;
`;

const ArrowRightIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

const CrossIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const LeaveSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const AccountRow = styled.a`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${Colors.border};
    border-radius: 0.5rem;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        border-color: ${Colors.primary};
    }

    /* Use :focus (not :focus-visible) so the clicked row stays highlighted
       through the full-page navigation. */
    &:focus {
        outline: none;
        border-color: ${Colors.primary};
        box-shadow: 0 0 0 2px ${Colors.primaryLight};
    }
`;

const AccountMain = styled.span`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const Arrow = styled.span`
    display: flex;
    align-items: center;
    width: 0;
    transform: scale(0);
    transform-origin: left center;
    transition: width 0.15s ease, transform 0.15s ease, margin-right 0.15s ease;
    color: ${Colors.primary};

    svg {
        width: 1rem;
        height: 1rem;
        display: block;
    }

    ${AccountRow}:hover &,
    ${AccountRow}:focus & {
        width: 1rem;
        margin-right: 0.4rem;
        transform: scale(1);
    }
`;

const AccountLabel = styled.span`
    font-size: 0.9375rem;
    font-weight: 500;
    color: ${Colors.textDark};
`;

const AccountRole = styled.span`
    font-size: 0.8125rem;
    color: ${Colors.textLight};
`;

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.5rem 0 1rem;
`;
